const TYPING_TARGET_SELECTOR = [
  "input",
  "textarea",
  "select",
  "[role='textbox']",
  "[role='combobox']",
  "[role='listbox']",
  "[role='option']",
  "[contenteditable]:not([contenteditable='false'])",
].join(", ")

function getTargetElement(target: EventTarget | null): Element | null {
  if (target instanceof Element) {
    return target
  }

  if (target instanceof Node) {
    return target.parentElement
  }

  return null
}

export function isKeyboardTypingTarget(target: EventTarget | null): boolean {
  const element = getTargetElement(target)

  if (!element) {
    return false
  }

  if (element instanceof HTMLElement && element.isContentEditable) {
    return true
  }

  return element.closest(TYPING_TARGET_SELECTOR) !== null
}

const MODAL_LAYER_SELECTOR = "[aria-modal='true']"
const LAYER_SELECTOR = [
  "[role='dialog']",
  "[role='alertdialog']",
  "[role='menu']",
].join(", ")

/**
 * True when the key lands inside a modal layer: an element marked aria-modal,
 * or a dialog or menu that makes the page behind it inert. Page shortcuts act
 * on the content that layer covers, so they stand down while it holds focus.
 * Radix sets no aria-modal; a modal dialog or menu disables pointer events on
 * the body while it is open, which a non-modal layer such as the docked drawer
 * never does, so the docked drawer leaves page shortcuts running.
 */
export function isModalLayerTarget(target: EventTarget | null): boolean {
  const element = getTargetElement(target)

  if (!element) {
    return false
  }

  if (element.closest(MODAL_LAYER_SELECTOR) !== null) {
    return true
  }

  return (
    element.closest(LAYER_SELECTOR) !== null &&
    element.ownerDocument.body.style.pointerEvents === "none"
  )
}

const LINK_SELECTOR = ["a[href]", "[role='link']"].join(", ")

const ACTIVATABLE_CONTROL_SELECTOR = [
  "button",
  "summary",
  "[role='button']",
  "[role='checkbox']",
  "[role='switch']",
  "[role='radio']",
  "[role='tab']",
  "[role='menuitem']",
  "[role='menuitemcheckbox']",
  "[role='menuitemradio']",
  "[role='row'][tabindex]",
].join(", ")

/**
 * True when Enter or Space lands on a focused control that activates on that
 * key, including a focusable table row that opens on it. The key belongs to the
 * control, so page shortcuts bound to it must not also run. Links activate on Enter only, so Space on a link stays available
 * to shortcuts. A control marked aria-disabled activates nothing, so its keys
 * stay available too.
 */
export function isKeyboardControlActivation(
  event: Pick<KeyboardEvent, "key" | "target">,
): boolean {
  if (event.key !== "Enter" && event.key !== " ") {
    return false
  }

  const element = getTargetElement(event.target)
  if (!element) {
    return false
  }

  const control =
    element.closest(ACTIVATABLE_CONTROL_SELECTOR) ??
    (event.key === "Enter" ? element.closest(LINK_SELECTOR) : null)

  return control != null && control.getAttribute("aria-disabled") !== "true"
}
